import { SortDirection, nextSortDirection } from "./sortOptions";

// The clickable column header every leads table sorts by. There were four
// copies - one per surface - which is how the saved-searches list ended up with
// an arrow rule the other three did not have and the lead panel's pickers ended
// up as the only tables with an unsorted state nobody could see was deliberate.
//
// The `sort` prop is the table's whole sort state, not this column's, because a
// header has to know whether it is the sorted one; `null` means the table is
// unsorted, which only a `three-way` header can produce.
export type SortState<K extends string> = { key: K; dir: SortDirection } | null;

interface SortHeaderBase {
  label: string;
  // Show a dimmed arrow on the columns that are NOT sorted, so every header
  // reads as sortable rather than only the active one.
  dimInactiveArrow?: boolean;
  tooltip?: string;
  // Merged into the `th`'s own style - a column width, or the sticky
  // positioning a scrolling table's header needs.
  style?: React.CSSProperties;
}

// `cycle` says what a click cycles through, and it types the sort state with
// it, because the two families of table here differ in whether "unsorted" is
// reachable at all:
//  - `two-way` (the boards): desc <-> asc, never unsorted. Their headers and
//    the "Sort by" dropdown drive ONE sort state, and unsorted is a state the
//    dropdown could not name (see SortBySelect) - so their setter is one that
//    does not take null, and the type says so.
//  - `three-way` (the pickers inside the lead panel): desc -> asc -> off. They
//    have no dropdown and start unsorted, so off is reachable anyway and
//    clicking back to it is the only way to return.
export type SortHeaderProps<K extends string> = SortHeaderBase &
  (
    | {
        cycle: "two-way";
        sortKey: K;
        sort: { key: K; dir: SortDirection };
        setSort: (next: { key: K; dir: SortDirection }) => void;
      }
    | {
        cycle: "three-way";
        sortKey: K;
        sort: SortState<K>;
        setSort: (next: SortState<K>) => void;
      }
  );

export const SortHeader = <K extends string>(props: SortHeaderProps<K>) => {
  const { label, sortKey, sort, dimInactiveArrow, tooltip, style } = props;
  const active = sort?.key === sortKey ? sort.dir : null;
  const onClick = () => {
    if (props.cycle === "three-way") {
      const next: SortDirection | null =
        active === null ? "desc" : active === "desc" ? "asc" : null;
      props.setSort(next === null ? null : { key: sortKey, dir: next });
      return;
    }
    props.setSort({
      key: sortKey,
      dir: nextSortDirection(sortKey, props.sort),
    });
  };
  return (
    <th
      style={{ cursor: "pointer", userSelect: "none", ...style }}
      onClick={onClick}
      title={tooltip}
    >
      {label}
      {dimInactiveArrow ? (
        <span style={{ marginLeft: 4, color: active ? "#495057" : "#ced4da" }}>
          {active === "asc" ? "▲" : "▼"}
        </span>
      ) : active === "desc" ? (
        " ▼"
      ) : active === "asc" ? (
        " ▲"
      ) : null}
    </th>
  );
};

export default SortHeader;
